import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Alert, Loader, Button } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import { CallStatusBadge } from './VoiceCallsPage';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * One call's audit record (`/app-voice-call/:id`) — status timeline, the transcript
 * turn-by-turn, the recording (streamed through the authenticated
 * /api/voice/calls/:id/recording proxy — recordings are private objects, no public
 * URL exists), the collected JSON against the goal schema, and the raw provider
 * analysis for debugging. Auto-refreshes while the call is still live.
 */
const LIVE_STATUSES = new Set(['pending', 'dialing', 'in_progress']);

function Meta({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase text-secondary-500">{label}</div>
      <div className="text-sm">{children ?? '—'}</div>
    </div>
  );
}

export default function VoiceCallDetail() {
  const { id } = useParams();
  const { fetchApi } = useAppApi();
  const [call, setCall] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => fetchApi('voiceCallShow', id)
    .then(setCall)
    .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
    .finally(() => setLoading(false));

  useEffect(() => {
    setLoading(true);
    load();
  }, [id]);

  useEffect(() => {
    if (!call || !LIVE_STATUSES.has(call.status)) return undefined;
    const timer = setInterval(load, 8000);
    return () => clearInterval(timer);
  }, [call && call.status]);

  const cancel = async () => {
    try {
      await fetchApi('voiceCallCancel', id);
      load();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  if (loading) return <PageShell><Loader text="Loading call…" /></PageShell>;
  if (err && !call) return <PageShell><Alert variant="error" title="Could not load">{err}</Alert></PageShell>;
  if (!call) return null;

  const collected = call.collected_data || {};
  const missing = Array.isArray(collected._missing) ? collected._missing : [];
  const collectedEntries = Object.entries(collected).filter(([k]) => k !== '_missing');

  return (
    <PageShell width="narrow" className="space-y-3">
      <PageHeader
        backTo="/app-voice-call"
        backLabel="All calls"
        title={<>Call to {call.to_number}</>}
        meta={<CallStatusBadge status={call.status} />}
        actions={(
          <>
            {call.status === 'pending' && (
              <Can module="calls" action="dial">
                <Button size="sm" variant="secondary" onClick={cancel}>Cancel</Button>
              </Can>
            )}
          </>
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}
      {call.error && <Alert variant="error" title="Call error">{call.error}</Alert>}

      <Card className="p-3 grid grid-cols-3 gap-3">
        <Meta label="Purpose">{call.purpose}</Meta>
        <Meta label="Outcome">{call.call_successful || '—'}</Meta>
        <Meta label="Duration">{call.duration_secs ? `${call.duration_secs}s` : '—'}</Meta>
        <Meta label="Agent">{call.agent_key || '—'}</Meta>
        <Meta label="Scheduled">{call.scheduled_at ? new Date(call.scheduled_at).toLocaleString() : 'immediate'}</Meta>
        <Meta label="Ended">{call.ended_at ? new Date(call.ended_at).toLocaleString() : '—'}</Meta>
      </Card>

      {call.transcript_summary && (
        <Card className="p-3">
          <h2 className="font-semibold mb-1">Summary</h2>
          <p className="text-sm">{call.transcript_summary}</p>
        </Card>
      )}

      {call.has_recording && (
        <Card className="p-3">
          <h2 className="font-semibold mb-2">Recording (audit)</h2>
          {/* Same-origin <audio> sends the auth cookie; the server streams the private object. */}
          <audio controls preload="none" className="w-full" src={`/api/voice/calls/${call.id}/recording`} />
        </Card>
      )}

      {collectedEntries.length > 0 && (
        <Card className="p-3">
          <h2 className="font-semibold mb-2">Collected data</h2>
          <table className="w-full text-sm">
            <tbody>
              {collectedEntries.map(([key, value]) => (
                <tr key={key} className="border-t border-[var(--app-border)]">
                  <td className="py-1.5 pr-4 font-mono text-xs text-secondary-500">{key}</td>
                  <td className="py-1.5">{String(value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {missing.length > 0 && (
            <p className="text-xs text-amber-700 mt-2">Not obtained on the call: {missing.join(', ')}</p>
          )}
        </Card>
      )}

      {Array.isArray(call.transcript) && call.transcript.length > 0 && (
        <Card className="p-3">
          <h2 className="font-semibold mb-2">Transcript</h2>
          <div className="space-y-2">
            {call.transcript.map((turn, i) => (
              <div key={i} className={`text-sm flex gap-2 ${turn.role === 'agent' ? '' : 'text-primary-700'}`}>
                <span className="font-semibold shrink-0 w-14">{turn.role === 'agent' ? 'Agent' : 'Caller'}</span>
                <span>{turn.message}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {call.analysis_raw && (
        <details>
          <summary className="cursor-pointer font-semibold py-2">Raw provider analysis</summary>
          <Card className="p-3">
            <pre className="text-xs overflow-x-auto">{JSON.stringify(call.analysis_raw, null, 2)}</pre>
          </Card>
        </details>
      )}
    </PageShell>
  );
}
